import React, { useMemo } from "react";
import { DialogContent, Grid, Typography, TextField, List, ListItem, makeStyles } from "@material-ui/core";
import EmailIcon from "@material-ui/icons/Email";

import { printAssetCategory } from "../../entities/AssetCategory";
import { BuilderAsset } from "../../entities/BuilderAsset";
import { useAuthenticatedUser } from "../../utilities/useAuthenticatedUser";

import FacebookLogo from "../../resources/facebookIcon.png";
import TwitterLogo from "../../resources/twitterIcon.png";

const useStyles = makeStyles(() => ({
    copyButton: {
        width: "100%",
        height: 51,
        backgroundColor: "#5bb2f6",
        marginBottom: 8,
        color: "#FFF",
        textAlign: "center",
        verticalAlign: "middle",
        "& span": {
            display: "inline-block",
            marginTop: 16,
        },
        cursor: "pointer",
    },
    urlInput: {
        maxHeight: 51,
        borderRadius: "0px",
        fontSize: "12px",
        [`& fieldset`]: {
            borderRadius: 0,
        },
    },
    listHorizontal: {
        display: "flex",
        flexDirection: "row",
        padding: 0,
    },
    listItem: {
        width: 70,
        "&:nth-of-type(2)": {
            width: 95,
        },
    },
    socialIcons: {
        width: 40,
        height: 40,
        marginTop: -5,
    },
    listItemSubContainer: {
        textAlign: "center",
    },
}));

interface Props {
    item: BuilderAsset;
    fromSlides: boolean;
    url: string;
    handleCopy: () => void;
}

export function SharedContent(props:Props){
    const {item, fromSlides, url} = props;
    const classes = useStyles();
    const user = useAuthenticatedUser();

    const encodedEmailUrl = useMemo(() => {
        let emailUrl = "mailto:?body=I want to share ";

        if (fromSlides) {
            emailUrl += `these slides`;
        } else {
            emailUrl += `this ${printAssetCategory(item.category)}`;
        }

        emailUrl += "  https://maker.sermoncentral.com/share/";

        if (fromSlides)
            emailUrl += "slide/";

        emailUrl += `${item.shareId}&subject=I want you to see this sermon`;

        return encodeURI(emailUrl);
    }, [props]);

    const encodedFacebookUrl = useMemo(() => {
        let facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}`;
    
        if (fromSlides) {
          facebookUrl += `&quote=Slides from ${item.name}`;
        } else {
          facebookUrl += `&quote=${item.name} by ${user.firstName} ${user.lastName}`;
        }
        return encodeURI(facebookUrl);
    }, [props])

    const encodedTwitterUrl = useMemo(() => {
        let twitterUrl = `https://twitter.com/share?ref_src=twsrc%5Etfw&url=${url}`;
    
        if (fromSlides) {
          twitterUrl += `&title=Slides from ${item.name}`;
        } else {
          twitterUrl += `&title=${item.name} by ${user.firstName} ${user.lastName}`;
        }
        
        return encodeURI(twitterUrl);
    }, [props])


    return (
      <DialogContent>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Typography style={{ marginBottom: 8 }} variant="subtitle2">
              Link for sharing
            </Typography>
          </Grid>
          <Grid item xs={9}>
            <TextField
              id="url"
              value={url}
              fullWidth
              InputProps={{
                readOnly: true,
                className: classes.urlInput,
                id: "urlInput",
              }}
              variant="outlined"
            />
          </Grid>
          <Grid item xs={3}>
            <div className={classes.copyButton} onClick={() => props.handleCopy()} >
              <span>COPY LINK</span>
            </div>
          </Grid>
        </Grid>
        <hr />
        <Typography style={{ marginBottom: 8, marginTop: 12 }} variant="subtitle2" >
          More sharing options
        </Typography>
        <div style={{ maxWidth: 300 }}>
          <List className={classes.listHorizontal}>
            <ListItem className={classes.listItem}>
              <div className={classes.listItemSubContainer}>
                <EmailIcon fontSize="large" />
                <a href={encodedEmailUrl}>Email</a>
              </div>
            </ListItem>
            <ListItem className={classes.listItem}>
              <div className={classes.listItemSubContainer}>
                <img alt="Facebook" className={classes.socialIcons} src={FacebookLogo} />
                <a href={encodedFacebookUrl} rel="noopener noreferrer" target="_blank">
                  Facebook
                </a>
              </div>
            </ListItem>
            <ListItem className={classes.listItem}>
              <div className={classes.listItemSubContainer}>
                <img alt="Twitter" className={classes.socialIcons} src={TwitterLogo} />
                <a
                  href={encodedTwitterUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  className="twitter-share-button"
                  data-show-count="false"
                >
                  Twitter
                </a>
              </div>
            </ListItem>
          </List>
        </div>
      </DialogContent>
    );
}